/* ===========================
   Wiredan App - Agri-Pulse Navbar
   Status: STABLE | HIGH-FIDELITY UI | SURGICAL BRAND REDIRECT | LOGISTICS INTEGRATED FOR ALL USERS
   File: src/components/Navbar.jsx
   =========================== */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  FiMenu, FiX, FiUser, FiSearch, FiShoppingBag, 
  FiBookOpen, FiTruck, FiGrid, FiShield, FiCreditCard, 
  FiLogOut, FiGlobe, FiActivity, FiPower, FiRefreshCw,
  FiMapPin
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useSatellite } from '../context/SatelliteContext'; 
import NotificationButton from './NotificationButton';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout, isAuthenticated } = useAuth();
  const { isNodeActive, isSyncing, togglePower } = useSatellite(); 
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setIsMenuOpen(false); }, [location]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/marketplace?search=${encodeURIComponent(searchQuery)}`);
      setIsMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    setIsMenuOpen(false);
  };

  const handleProtectedLink = (path) => {
    const allowed = ['/dashboard', '/profile', '/education-hub', '/transactions', '/settings', '/agri/setup', '/identity-verification', '/kyc', '/logistics'];
    const isVerified = user?.kyc_verified === 1 || user?.kyc_verified === true;

    if (!isVerified && !allowed.includes(path)) {
      navigate('/identity-verification');
    } else {
      navigate(path);
    }
    setIsMenuOpen(false);
  };

  const renderAccountId = () => {
    if (!user?.id && !user?.uuid) return "SYNCING...";
    const displayId = user?.uuid || user?.id;
    return displayId.length > 15 ? `${displayId.substring(0, 15)}...` : displayId;
  };

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-sm border-b border-slate-100 font-sans">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        
        <div 
          onClick={() => handleProtectedLink('/agri/setup')}
          className="flex items-center space-x-3 cursor-pointer group"
        >
          <div className="w-9 h-9 bg-[#F25C05] rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-orange-100 transform -rotate-3 group-hover:rotate-0 transition-transform">
            <FiGlobe size={20} />
          </div>
          <div className="flex flex-col">
            <h1 className="text-lg font-black tracking-tighter text-[#0B1221] leading-none">WIREDAN</h1>
            <span className="text-[8px] font-black text-[#F25C05] uppercase tracking-[0.3em]">Agri-Pulse</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* LOGISTICS & GPS DISPATCH LINK - VISIBLE TO ALL AUTHENTICATED BUYERS, SELLERS & DRIVERS */}
          {isAuthenticated && (
            <button
              onClick={() => handleProtectedLink('/logistics')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-orange-50 border border-orange-200 text-[#F25C05] hover:bg-[#F25C05] hover:text-white transition-all active:scale-95 font-black text-[10px] uppercase tracking-wider"
              title="GPS Dispatch & Tracking Router"
            >
              <FiMapPin size={15} />
              <span className="hidden sm:inline">Dispatch</span>
            </button>
          )}

          {isAuthenticated && (
            <button 
              onClick={togglePower}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all active:scale-95 ${
                isNodeActive ? 'bg-[#EAB308] border-[#CA8A04] text-[#422006]' : 'bg-slate-50 border-slate-100 text-slate-400'
              }`}
            >
              {isSyncing ? <FiRefreshCw className="animate-spin" size={16} /> : <FiPower size={16} />}
              <span className="text-[10px] font-black uppercase tracking-wider hidden xs:block">
                {isSyncing ? 'Sync' : isNodeActive ? 'On' : 'Off'}
              </span>
            </button>
          )}

          {isAuthenticated && (
            <div className="flex items-center">
              <NotificationButton />
            </div>
          )}
          
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className="p-2.5 rounded-xl bg-slate-50 text-[#0B1221] hover:bg-slate-100 transition-all active:scale-95 border border-slate-100"
          >
            {isMenuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-white flex flex-col p-6 animate-in slide-in-from-right duration-300">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
               <div className="w-6 h-6 bg-[#0B1221] rounded flex items-center justify-center text-white text-[10px] font-bold">N</div>
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Nexus Navigation</span>
            </div>
            <button onClick={() => setIsMenuOpen(false)} className="p-2 bg-slate-100 rounded-full text-slate-500 hover:bg-red-50 hover:text-red-500 transition-colors">
              <FiX size={20} />
            </button>
          </div>
          
          <div className="flex flex-col h-full overflow-y-auto max-w-sm mx-auto w-full pb-32">
            {isAuthenticated && (
              <div className="mb-6 bg-[#0B1221] p-5 rounded-[24px] shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#F25C05] opacity-10 rounded-full blur-3xl -mr-10 -mt-10"></div>
                <div className="flex justify-between items-center mb-1">
                  <p className="text-[9px] font-black text-orange-400 tracking-[0.2em] uppercase">Global : ID</p>
                  {/* PREMIUM BADGE */}
                  {user?.is_premium === 1 && (
                    <div className="flex items-center gap-1 bg-amber-500 text-white px-2 py-0.5 rounded-full">
                      <FiShield size={10} />
                      <span className="text-[8px] font-bold uppercase tracking-wider">Premium</span>
                    </div>
                  )}
                </div>
                <p className="font-bold text-white font-mono text-[13px] tracking-tight">{renderAccountId()}</p>
              </div>
            )}

            <form onSubmit={handleSearch} className="relative mb-6">
              <input 
                type="text" 
                placeholder="SEARCH MARKET..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                className="w-full p-4 pl-12 bg-slate-50 rounded-[20px] font-bold uppercase text-[10px] outline-none border-2 border-transparent focus:border-[#F25C05] focus:bg-white transition-all shadow-sm" 
              />
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            </form>

            {isAuthenticated ? (
              <>
                <div className="flex flex-col gap-1 mb-6">
                  <MenuLink icon={<FiGrid size={18} className="text-orange-500"/>} label="Dashboard" onClick={() => handleProtectedLink('/dashboard')} />
                  <MenuLink icon={<FiActivity size={18} className="text-[#F25C05]"/>} label="Wiredan Agri-Pulse" onClick={() => handleProtectedLink('/agri/setup')} />
                  <MenuLink icon={<FiShoppingBag size={18} className="text-emerald-500"/>} label="Marketplace" onClick={() => handleProtectedLink('/marketplace')} />
                  <MenuLink icon={<FiTruck size={18} className="text-blue-500"/>} label="Orders" onClick={() => handleProtectedLink('/orders')} />
                  
                  {/* DRAWER LOGISTICS & GPS TRACKING MENU LINK - VISIBLE TO ALL USERS */}
                  <MenuLink 
                    icon={<FiMapPin size={18} className="text-indigo-500"/>} 
                    label="Logistics & GPS Dispatch" 
                    onClick={() => handleProtectedLink('/logistics')} 
                  />

                  <MenuLink icon={<FiBookOpen size={18} className="text-amber-500"/>} label="Education" onClick={() => handleProtectedLink('/education-hub')} />
                  <MenuLink icon={<FiUser size={18} className="text-slate-500"/>} label="Profile" onClick={() => handleProtectedLink('/profile')} />
                </div>

                <div className="grid grid-cols-2 gap-3 mb-8">
                  <button onClick={() => handleProtectedLink('/identity-verification')} className="flex flex-col items-center gap-2 p-5 rounded-3xl bg-[#FFF9F5] border-2 border-orange-50 text-[#C2410C] hover:border-orange-200 transition-all font-black text-[9px] uppercase tracking-wider shadow-sm">
                    <FiShield size={24}/> KYC VERIFY
                  </button>
                  <button onClick={() => handleProtectedLink('/settings')} className="flex flex-col items-center gap-2 p-5 rounded-3xl bg-[#F0FDF4] border-2 border-emerald-50 text-[#15803D] hover:border-emerald-200 transition-all font-black text-[9px] uppercase tracking-wider shadow-sm">
                    <FiCreditCard size={24}/> PAYMENTS
                  </button>
                </div>

                <div className="border-t border-slate-100 pt-6">
                  <button 
                    onClick={handleLogout} 
                    className="flex items-center justify-between w-full p-5 bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white rounded-[20px] font-black uppercase text-[10px] tracking-[0.2em] transition-all group border border-rose-100 active:scale-[0.98]"
                  >
                    <span>Terminate Session</span>
                    <FiLogOut size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-3 mt-4">
                <Link to="/login" className="block p-5 rounded-2xl bg-[#0B1221] text-white text-center font-black text-[10px] uppercase tracking-[0.2em] active:scale-95 transition-transform">LOGIN</Link>
                <Link to="/signup" className="block p-5 rounded-2xl bg-[#F25C05] text-white text-center font-black text-[10px] uppercase tracking-[0.2em] active:scale-95 transition-transform shadow-lg shadow-orange-100">SIGN UP</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

const MenuLink = ({ icon, label, onClick }) => (
  <button 
    onClick={onClick} 
    className="flex items-center gap-4 w-full p-4 rounded-2xl hover:bg-slate-50 font-black text-[11px] uppercase text-[#0B1221] tracking-[0.1em] transition-all group active:translate-x-1"
  >
    <span className="p-2 bg-white rounded-lg shadow-sm group-hover:shadow-md transition-all">{icon}</span>
    {label}
  </button>
);

export default Navbar;